import { createMemo, createSignal, For, Index, onCleanup, onMount, Show } from "solid-js";

// Rendu de matrice commun aux matrices projets × leviers, projets × projets,
// filière × levier et leviers × leviers — portage de poc/src/matrice.js.
//
// Trois types de case (ADR-0003 du POC) :
//   presence   — booléen : une pastille présente ou absente, jamais deux teintes ;
//   sequentiel — grandeur continue : une teinte, du clair au foncé ;
//   effectif   — comptage : la valeur est écrite, l'aplat ne fait que hiérarchiser.

export interface EtiquetteMatrice {
  libelle: string;
  /** Texte complet, en infobulle. */
  titre?: string;
  /** Repères de bioagresseur en marge (une teinte par classe visée). */
  couleurs?: string[];
}

export type TypeMatrice = "presence" | "sequentiel" | "effectif";

// Taille du texte des matrices, en pixels à l'écran : les marges et la hauteur
// minimale des lignes en découlent.
const POLICE = 12.5;
const MARGE_BAS = 10;
const LONGUEUR_ETIQUETTE = 30;

/** Un libellé de 52 caractères en en-tête mangerait la vue ; le complet reste en infobulle. */
const raccourcir = (texte: string) =>
  texte.length <= LONGUEUR_ETIQUETTE
    ? texte
    : `${texte.slice(0, LONGUEUR_ETIQUETTE - 1).replace(/[\s,;:.-]+$/, "")}…`;

interface Geometrie {
  gauche: number;
  haut: number;
  largeurCase: number;
  hauteurCase: number;
  largeur: number;
  hauteur: number;
  vertical: boolean;
}

function geometrie(
  lignes: EtiquetteMatrice[],
  colonnes: EtiquetteMatrice[],
  largeurDispo: number,
  hauteurCible: number,
  diagonale: boolean,
): Geometrie {
  const libelleMax = lignes.reduce((m, l) => Math.max(m, l.libelle.length), 1);
  // Sur petit écran, les acronymes ont un peu plus de place : rognés, ils ne se lisent plus.
  const partGauche = largeurDispo < 520 ? 0.42 : 0.34;
  const gauche = Math.min(Math.max(70, libelleMax * POLICE * 0.56 + 22), largeurDispo * partGauche);
  // Au-delà d'une douzaine de colonnes, ou quand elles sont trop étroites pour
  // des étiquettes obliques (petit écran), les étiquettes passent à la verticale.
  const largeurEstimee = (largeurDispo - gauche - 14) / Math.max(1, colonnes.length);
  const vertical = colonnes.length > 12 || largeurEstimee < POLICE * 3.2;
  const colMax = colonnes.reduce((m, c) => Math.max(m, raccourcir(c.libelle).length), 1);
  const haut = vertical
    ? Math.max(46, colMax * POLICE * 0.56 + 12)
    : Math.max(46, colMax * POLICE * 0.44 + 16);
  // Sous ~1,3 fois la police les étiquettes se chevauchent : la matrice défile plutôt.
  let hauteurCase = Math.max(
    Math.ceil(POLICE * 1.3),
    Math.min((Math.max(240, hauteurCible) - haut - MARGE_BAS) / Math.max(1, lignes.length), 38),
  );
  // Peu de colonnes : elles s'élargissent pour occuper la zone, sans s'étirer à l'excès.
  const plafond = colonnes.length <= 12 ? 120 : 40;
  // Une étiquette oblique déborde à droite de sa colonne.
  const droite = vertical
    ? 14
    : Math.max(14, Math.cos((38 * Math.PI) / 180) * colMax * POLICE * 0.56);
  let largeurCase = Math.max(
    9,
    Math.min((largeurDispo - gauche - droite) / Math.max(1, colonnes.length), plafond),
  );
  // Matrice carrée → cases carrées, sinon la diagonale n'en est plus une.
  if (diagonale) largeurCase = hauteurCase = Math.min(largeurCase, hauteurCase);
  return {
    gauche,
    haut,
    largeurCase,
    hauteurCase,
    largeur: gauche + colonnes.length * largeurCase + droite,
    hauteur: haut + lignes.length * hauteurCase + MARGE_BAS,
    vertical,
  };
}

export function Matrice(props: {
  lignes: EtiquetteMatrice[];
  colonnes: EtiquetteMatrice[];
  valeur: (i: number, j: number) => number | null;
  type: TypeMatrice;
  /** Libellé accessible de la figure. */
  titre: string;
  hauteurCible?: number;
  /** Borne haute de l'échelle ; sinon la plus forte valeur présente. */
  max?: number;
  diagonale?: boolean;
  /** Case non dessinée : matrices triangulaires. */
  masquer?: (i: number, j: number) => boolean;
  /** Ligne en retrait (effectif trop faible). */
  attenuer?: (i: number) => boolean;
  /** Lignes mises en avant par les filtres ; null = aucune mise en avant. */
  enAvant?: ((i: number) => boolean) | null;
  /** Ligne sélectionnée. */
  ligneChoisie?: number | null;
  surLigne?: (i: number) => void;
  infobulle?: (i: number, j: number, v: number | null) => string;
  /** Donne le SVG à l'appelant, pour l'export. */
  refSvg?: (svg: SVGSVGElement) => void;
}) {
  let conteneur: HTMLDivElement | undefined;
  const [largeurDispo, setLargeurDispo] = createSignal(900);
  onMount(() => {
    if (!conteneur) return;
    const mesurer = () => {
      const l = conteneur?.getBoundingClientRect().width ?? 0;
      if (l > 0) setLargeurDispo(Math.max(320, Math.round(l)));
    };
    mesurer();
    if (typeof ResizeObserver === "undefined") return;
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(conteneur);
    onCleanup(() => observateur.disconnect());
  });

  const g = createMemo(() =>
    geometrie(
      props.lignes,
      props.colonnes,
      largeurDispo(),
      props.hauteurCible ?? 620,
      props.diagonale ?? false,
    ),
  );

  // Échelle bornée aux valeurs présentes, jamais à [0, 1].
  const maximum = createMemo(() => {
    if (props.max !== undefined) return props.max;
    let m = 0;
    props.lignes.forEach((_, i) =>
      props.colonnes.forEach((_c, j) => {
        const v = props.valeur(i, j);
        if (v !== null && v > m) m = v;
      }),
    );
    return m;
  });
  // Sur un comptage, le nombre porte l'information : l'aplat reste clair pour rester lisible.
  const opaciteMax = () => (props.type === "effectif" ? 0.48 : 0.92);
  const opacite = (v: number) =>
    (
      0.08 +
      (opaciteMax() - 0.08) * (maximum() > 0 ? Math.min(1, Math.max(0, v / maximum())) : 0)
    ).toFixed(3);

  const [vise, setVise] = createSignal<{ i: number; j: number } | null>(null);
  const viser = (e: PointerEvent) => {
    const svg = e.currentTarget as SVGElement;
    const boite = svg.ownerSVGElement?.getBoundingClientRect() ?? svg.getBoundingClientRect();
    const echelle = boite.width > 0 ? g().largeur / boite.width : 1;
    const x = (e.clientX - boite.left) * echelle - g().gauche;
    const y = (e.clientY - boite.top) * echelle - g().haut;
    const j = Math.floor(x / g().largeurCase);
    const i = Math.floor(y / g().hauteurCase);
    const dedans = i >= 0 && j >= 0 && i < props.lignes.length && j < props.colonnes.length;
    setVise(dedans && !props.masquer?.(i, j) ? { i, j } : null);
  };
  const texteInfobulle = createMemo(() => {
    const v = vise();
    if (!v || !props.infobulle) return "";
    return props.infobulle(v.i, v.j, props.valeur(v.i, v.j));
  });

  const marge = () => Math.min(2, g().largeurCase * 0.12);

  return (
    <div ref={(e) => (conteneur = e)} class="matrice">
      <svg
        ref={(e) => props.refSvg?.(e)}
        class="matrice__dessin"
        viewBox={`0 0 ${g().largeur.toFixed(1)} ${g().hauteur.toFixed(1)}`}
        width={g().largeur.toFixed(1)}
        height={g().hauteur.toFixed(1)}
        role="img"
        aria-label={props.titre}
        onPointerLeave={() => setVise(null)}
      >
        <g data-export="non">
          <Show when={vise()}>
            {(v) => (
              <>
                <rect
                  class="matrice__bande"
                  x={0}
                  y={g().haut + v().i * g().hauteurCase}
                  width={g().largeur}
                  height={g().hauteurCase}
                />
                <rect
                  class="matrice__bande"
                  x={g().gauche + v().j * g().largeurCase}
                  y={0}
                  width={g().largeurCase}
                  height={g().hauteur}
                />
              </>
            )}
          </Show>
        </g>

        <Index each={props.colonnes}>
          {(c, j) => {
            const x = () => g().gauche + j * g().largeurCase + g().largeurCase / 2;
            return (
              <text
                class="matrice__colonne"
                font-size={String(POLICE)}
                transform={`translate(${x().toFixed(1)},${g().haut - 6}) rotate(${g().vertical ? -90 : -38})`}
              >
                {raccourcir(c().libelle)}
                <title>{c().titre ?? c().libelle}</title>
              </text>
            );
          }}
        </Index>

        <Index each={props.lignes}>
          {(l, i) => {
            const y = () => g().haut + i * g().hauteurCase;
            const classes = () => ({
              "matrice__ligne--attenuee": props.attenuer?.(i) ?? false,
              "matrice__ligne--hors": props.enAvant ? !props.enAvant(i) : false,
              "matrice__ligne--choisie": props.ligneChoisie === i,
            });
            return (
              <g class="matrice__ligne" classList={classes()}>
                {/* Repère de bioagresseur en marge : la couleur n'encode jamais la case. */}
                <For each={l().couleurs ?? []}>
                  {(couleur, k) => {
                    const hautRepere = () => Math.max(3, g().hauteurCase * 0.64);
                    const pas = () => hautRepere() / (l().couleurs?.length ?? 1);
                    return (
                      <rect
                        x={g().gauche - 7}
                        y={y() + g().hauteurCase * 0.18 + k() * pas()}
                        width={3.5}
                        height={pas()}
                        fill={couleur}
                      />
                    );
                  }}
                </For>
                <text
                  class="matrice__etiquette"
                  classList={{ "matrice__etiquette--cliquable": Boolean(props.surLigne) }}
                  x={g().gauche - 12}
                  y={y() + g().hauteurCase / 2 + POLICE * 0.35}
                  font-size={String(POLICE)}
                  text-anchor="end"
                  onClick={() => props.surLigne?.(i)}
                >
                  {l().libelle}
                  <Show when={l().titre}>
                    <title>{l().titre}</title>
                  </Show>
                </text>
                <Index each={props.colonnes}>
                  {(_c, j) => {
                    const v = () => props.valeur(i, j);
                    const x = () => g().gauche + j * g().largeurCase;
                    return (
                      <Show when={!props.masquer?.(i, j)}>
                        <Show
                          when={!(props.diagonale && i === j)}
                          fallback={
                            <rect
                              class="matrice__case matrice__case--diagonale"
                              x={x() + marge() / 2}
                              y={y() + marge() / 2}
                              width={g().largeurCase - marge()}
                              height={g().hauteurCase - marge()}
                              rx={1.5}
                            />
                          }
                        >
                          <Show
                            when={props.type !== "presence"}
                            fallback={
                              <circle
                                class="matrice__pastille"
                                classList={{ "matrice__pastille--vide": !v() }}
                                cx={x() + g().largeurCase / 2}
                                cy={y() + g().hauteurCase / 2}
                                r={Math.max(2.2, Math.min(g().largeurCase, g().hauteurCase) * 0.3)}
                              />
                            }
                          >
                            <rect
                              class="matrice__case"
                              classList={{ "matrice__case--nulle": v() === null }}
                              x={x() + marge() / 2}
                              y={y() + marge() / 2}
                              width={g().largeurCase - marge()}
                              height={g().hauteurCase - marge()}
                              rx={1.5}
                              fill-opacity={v() === null ? undefined : opacite(v() ?? 0)}
                            />
                            <Show when={props.type === "effectif" && v()}>
                              <text
                                class="matrice__valeur"
                                x={x() + g().largeurCase / 2}
                                y={y() + g().hauteurCase / 2 + POLICE * 0.35}
                                font-size={String(Math.min(POLICE, g().hauteurCase * 0.6))}
                                text-anchor="middle"
                              >
                                {v()}
                              </text>
                            </Show>
                          </Show>
                        </Show>
                      </Show>
                    );
                  }}
                </Index>
              </g>
            );
          }}
        </Index>

        {/* Une seule zone de pointage pour toutes les cases : viser une pastille de 4 px est impossible. */}
        <rect
          data-export="non"
          x={g().gauche}
          y={g().haut}
          width={props.colonnes.length * g().largeurCase}
          height={props.lignes.length * g().hauteurCase}
          fill="transparent"
          onPointerMove={viser}
          onClick={() => {
            const v = vise();
            if (v) props.surLigne?.(v.i);
          }}
          style={{ cursor: props.surLigne ? "pointer" : "default" }}
        />
      </svg>
      <Show when={vise() && texteInfobulle()}>
        <div
          class="matrice__infobulle"
          style={{
            // Le SVG est dessiné à sa taille naturelle : positions en pixels du dessin.
            left: `${g().gauche + ((vise()?.j ?? 0) + 0.5) * g().largeurCase}px`,
            top: `${g().haut + (vise()?.i ?? 0) * g().hauteurCase - 4}px`,
          }}
          aria-hidden="true"
        >
          {texteInfobulle()}
        </div>
      </Show>
    </div>
  );
}
